/**
 * @description Component-TradeMarketForm
 */
import // Trans,
'react-i18next';
import './index.less';

import { FETCHING_STATUS } from '@/constants';
import { TRADE_LEVERAGE_THRESHOLDS } from '@/constants/global';
import { TRADE_TYPE } from '@/constants/trade';
import { WrappedBigNumber } from '@/entities/WrappedBigNumber';
import { useMainPosition } from '@/features/account/positionHook';
import { useAvailableTokenBalance } from '@/features/balance/hook';
import { useGlobalConfig } from '@/features/global/hooks';
import { useCurrentPairFromUrl } from '@/features/pair/hook';
import { clearMarketTradeSimulation, setTradeFormType, simulateMarketTrade } from '@/features/trade/actions';
import {
  useMarketFormStateStatus,
  useMarketSimulation,
  useTradeDefaultLeverage,
  useTradeDefaultSide,
  useTradeMaxLeverage,
  useTradeSide,
  useTradeStoreTransformed,
} from '@/features/trade/hooks';
import { useSDK } from '@/features/web3/hook';
import { useAppDispatch } from '@/hooks';
import { useChainId, useUserAddr } from '@/hooks/web3/useChain';
import { toBN } from '@/utils/numberUtil';
import { useDebounceEffect, useDebounceFn } from 'ahooks';
import { Form } from 'antd';
import classNames from 'classnames';
import React, { FC, useCallback, useEffect, useMemo } from 'react';

import AmountAdjustor from '@/components/AmountAdjustor';
import { useIsFetchedPortfolioList } from '@/features/portfolio/hook';
import { Leverage } from '@synfutures/sdks-perp';
import TradeFormDetails from '../TradeDetail/TradeFormDetails';
import TradeFormAlert from '../TradeFormAlert';
import TradeFormFooter from '../tradeformFooter';

interface IPropTypes {
  children?: React.ReactNode;
  className?: string;
}

const TradeMarketFormSimple: FC<IPropTypes> = function ({ className }) {
  const dispatch = useAppDispatch();
  // const { t } = useTranslation();
  const chainId = useChainId();
  const tradeSide = useTradeSide(chainId);
  // const marketFormState = useMarketFormState(chainId);
  const currentPair = useCurrentPairFromUrl(chainId);
  const sdkContext = useSDK(chainId);
  useTradeDefaultSide(currentPair);
  const maxLeverage = useTradeMaxLeverage(currentPair?.maxLeverage);
  const defaultLeverage = useTradeDefaultLeverage(currentPair?.maxLeverage);
  const userAddr = useUserAddr();
  const marketFormStatus = useMarketFormStateStatus(chainId);
  const { tradeSize, leverage, setPairLeverage } = useTradeStoreTransformed();
  const currentPosition = useMainPosition(chainId, userAddr, currentPair?.id);
  const tradeSimulation = useMarketSimulation(chainId);
  const { slippage } = useGlobalConfig(chainId);
  const isFetchedPortfolio = useIsFetchedPortfolioList(chainId, userAddr);

  const availableBalance = useAvailableTokenBalance(currentPair?.rootInstrument?.marginToken?.address, chainId, true);
  const getIsClosing = useCallback(
    (amount?: string) => {
      amount = amount || tradeSize;
      if (currentPair && currentPosition) {
        const adjustTradeSide = tradeSide;
        return currentPosition?.side !== adjustTradeSide && currentPosition?.wrappedSize.abs().eq(amount);
      }
    },
    [currentPair, currentPosition, tradeSize, tradeSide],
  );
  const isClosing = useMemo(() => getIsClosing(), [getIsClosing]);

  const fetchSimulation = useCallback(() => {
    if (chainId && currentPosition && sdkContext && userAddr) {
      // if (WrappedBigNumber.from(tradeSize).lte(0)) {
      // dispatch(clearMarketTradeSimulation({ chainId }));
      // return;
      // }

      dispatch(
        simulateMarketTrade({
          chainId,
          sdkContext,
          position: currentPosition,
          tradeSide,
          base: tradeSize,
          slippage: Number(slippage),
          userAddr,
          leverage: leverage?.toString() || '',
          maxLeverage,
        }),
      );
    }
  }, [chainId, currentPosition, sdkContext, userAddr, tradeSize, dispatch, tradeSide, slippage, leverage, maxLeverage]);

  const { run: simulateTradeDebounced } = useDebounceFn(
    () => {
      fetchSimulation();
    },
    { wait: 100 },
  );

  const simulateFn = useCallback(() => {
    // chainId && dispatch(clearMarketTradeSimulation({ chainId }));
    if (!toBN(tradeSize || 0).eq(0)) {
      simulateTradeDebounced();
    }
  }, [tradeSize, leverage, simulateTradeDebounced]);

  useEffect(() => {
    if (chainId && userAddr && currentPair?.id && isFetchedPortfolio) {
      let leverageFinal =
        leverage ||
        (currentPosition?.hasPosition
          ? currentPosition?.wrapAttribute('leverageWad').formatNormalNumberString(1)
          : defaultLeverage.toString());
      // make sure leverage is normal number
      if (WrappedBigNumber.from(leverageFinal).lt(TRADE_LEVERAGE_THRESHOLDS.MIN)) {
        leverageFinal = TRADE_LEVERAGE_THRESHOLDS.MIN.toString();
      }
      if (leverageFinal !== Leverage && currentPosition?.rootPair.id) {
        setPairLeverage(userAddr, chainId, currentPosition?.rootPair.id, leverageFinal);
      }
    }
    // must dep on all three
  }, [
    chainId,
    currentPosition?.rootPair.id,
    currentPosition?.id,
    isFetchedPortfolio,
    userAddr,
    currentPair?.id,
    leverage,
    currentPosition,
    defaultLeverage,
    setPairLeverage,
  ]);

  const onNativeTokenDepositClick = useCallback(() => {
    chainId &&
      dispatch(
        setTradeFormType({
          chainId,
          tradeType: TRADE_TYPE.DEPOSIT_NATIVE,
        }),
      );
  }, [chainId, dispatch]);

  const onMaxBalanceClick = useCallback(
    (balance: WrappedBigNumber) => {
      if (chainId && currentPosition && sdkContext && userAddr) {
        if (WrappedBigNumber.from(tradeSize).lte(0)) {
          dispatch(clearMarketTradeSimulation({ chainId }));
          return;
        }
        dispatch(
          simulateMarketTrade({
            chainId,
            sdkContext,
            position: currentPosition,
            base: tradeSize,
            tradeSide,
            slippage: Number(slippage),
            userAddr,
            margin: balance.stringValue,
            maxLeverage,
          }),
        );
      }
    },
    [chainId, currentPosition, sdkContext, userAddr, dispatch, tradeSize, tradeSide, slippage, maxLeverage],
  );

  useEffect(() => {
    // re simulate only when amount exists
    if (marketFormStatus !== FETCHING_STATUS.FETCHING) {
      simulateFn();
    }
  }, [slippage]);

  useEffect(() => {
    //  simulate when fairPrice change
    if (marketFormStatus !== FETCHING_STATUS.FETCHING) {
      simulateFn();
    }
    // only dep on this
  }, [currentPair?.fairPrice._hex]);

  useEffect(() => {
    if (marketFormStatus !== FETCHING_STATUS.FETCHING) {
      //  simulate when balance change
      simulateFn();
    }
  }, [availableBalance?.stringValue]);
  useDebounceEffect(() => {
    if (marketFormStatus !== FETCHING_STATUS.FETCHING) {
      simulateFn();
    }
  }, [tradeSide, tradeSize, leverage]);

  useEffect(() => {
    if (marketFormStatus !== FETCHING_STATUS.FETCHING) {
      simulateFn();
    }
  }, [marketFormStatus]);
  const footer = useMemo(() => <TradeFormFooter tradeType={TRADE_TYPE.MARKET} />, []);

  const onBaseChange = useCallback((value: string) => {
    console.log(`MarketForm onBaseChange`, value);
    // chainId && dispatch(clearMarketTradeSimulation({ chainId }));
  }, []);
  const onQuoteChange = useCallback((value: string) => {
    console.log(`MarketForm onQuoteChange`, value);
    // chainId && dispatch(clearMarketTradeSimulation({ chainId }));
  }, []);
  return (
    <Form className={classNames('syn-trade-market-form', className)}>
      <AmountAdjustor
        disabled={marketFormStatus === FETCHING_STATUS.FETCHING}
        currentPair={currentPair}
        onBaseChange={onBaseChange}
        onQuoteChange={onQuoteChange}
      />

      {footer}
      <TradeFormAlert
        chainId={chainId}
        tradeType={TRADE_TYPE.MARKET}
        isClosing={isClosing}
        tradeSimulation={tradeSimulation}
        marginToken={currentPair?.rootInstrument?.marginToken}
        onMaxBalanceClick={onMaxBalanceClick}
        onNativeTokenDepositClick={onNativeTokenDepositClick}
        currentPair={currentPair}
        simpleErrorMode
      />
      {toBN(tradeSize || 0).gt(0) && (
        <TradeFormDetails
          simulation={tradeSimulation?.data}
          marginToken={currentPair?.rootInstrument.marginToken}
          fairPrice={currentPair?.wrapAttribute('fairPrice')}
          isLoading={false}
          chainId={chainId}
          tradeSide={tradeSide}
          currentPair={currentPair}
        />
      )}
    </Form>
  );
};

export default TradeMarketFormSimple;
